import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Briefcase, Fingerprint, Sparkles, Users, Sparkles as Spark } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { SiteBreadcrumbs } from "@/components/marketing/SiteBreadcrumbs";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/hero-card.webp";

export const Route = createFileRoute("/el/platform/")({
  head: () => ({
    meta: [
      { title: "Πλατφόρμα — banana.cards" },
      { name: "description", content: "Η ολοκληρωμένη πλατφόρμα Banana: ψηφιακή ταυτότητα και κοινοποίηση, σουίτα AI, ενσωματωμένο CRM και καθημερινές επιχειρηματικές εφαρμογές που μετατρέπουν τα αγγίγματα σε έσοδα." },
      { property: "og:title", content: "Πλατφόρμα — banana.cards" },
      { property: "og:description", content: "Ταυτότητα και κοινοποίηση, AI, CRM και εφαρμογές διαχείρισης της επιχείρησής σας — μία πλατφόρμα που αναπτύσσεται μαζί σας." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "el_GR" },
      { property: "og:url", content: "https://banana.cards/el/platform" },
    ],
    links: alternateLinkList("/platform", "el"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Η πλατφόρμα Banana",
          description: "Η ολοκληρωμένη πλατφόρμα Banana: ψηφιακή ταυτότητα και κοινοποίηση, σουίτα AI, ενσωματωμένο CRM και καθημερινές επιχειρηματικές εφαρμογές που μετατρέπουν τα αγγίγματα σε έσοδα.",
          url: "https://banana.cards/el/platform",
          isPartOf: { "@type": "WebSite", name: "banana.cards", url: "https://banana.cards/el" },
        }),
      },
    ],
  }),
  component: Page,
});

type Pillar = {
  title: string;
  href: string;
  icon: LucideIcon;
  desc: string;
  links: { label: string; href: string }[];
};

const pillars: Pillar[] = [
  {
    title: "Ταυτότητα & κοινοποίηση",
    href: "/el/platform/identity-and-sharing",
    icon: Fingerprint,
    desc: "Η ψηφιακή επαγγελματική κάρτα σας, premium προϊόντα NFC και κάθε κανάλι κοινοποίησης — QR, ψηφιακό πορτοφόλι, σύνδεσμοι, υπογραφές email.",
    links: [
      { label: "Ψηφιακές επαγγελματικές κάρτες", href: "/el/platform/cards" },
      { label: "Προϊόντα NFC", href: "/el/platform/nfc" },
      { label: "Δικτύωση & κοινοποίηση", href: "/el/platform/networking" },
    ],
  },
  {
    title: "Σουίτα AI",
    href: "/el/platform/ai-suite",
    icon: Sparkles,
    desc: "Το AI Brain θυμάται κάθε επαφή, ενώ ο εμπλουτισμός δεδομένων και τα εργαλεία σύνταξης, μετάφρασης και βαθμολόγησης δουλεύουν για εσάς.",
    links: [
      { label: "Μνήμη AI Brain", href: "/el/platform/ai" },
      { label: "Αυτόματος εμπλουτισμός δεδομένων", href: "/el/platform/ai-enrichment" },
      { label: "Σύνταξη, μετάφραση, βαθμολόγηση", href: "/el/platform/ai-tools" },
    ],
  },
  {
    title: "CRM",
    href: "/el/platform/crm",
    icon: Users,
    desc: "Καταγράψτε κάθε δυνητικό πελάτη, προωθήστε τις συμφωνίες μέσα από έξυπνες ροές πωλήσεων και δείτε τι αποδίδει σε πραγματικό χρόνο.",
    links: [
      { label: "Επαφές & καταγραφή δυνητικών πελατών", href: "/el/platform/contacts" },
      { label: "Ροές πωλήσεων & εργασίες", href: "/el/platform/pipelines" },
      { label: "Αναλυτικά στοιχεία σε πραγματικό χρόνο", href: "/el/platform/analytics" },
    ],
  },
  {
    title: "Διαχειριστείτε την επιχείρησή σας",
    href: "/el/platform/run-your-business",
    icon: Briefcase,
    desc: "Κρατήσεις, συναντήσεις, συμβάσεις, αυτοματισμοί, αξιολογήσεις, εκδηλώσεις, εργασίες — με εγκατάσταση με ένα κλικ.",
    links: [
      { label: "Κρατήσεις & συναντήσεις", href: "/el/platform/bookings" },
      { label: "Συμβάσεις & ηλεκτρονική υπογραφή", href: "/el/platform/contracts" },
      { label: "Αυτοματισμοί & αξιολογήσεις", href: "/el/platform/automations" },
    ],
  },
];

function Page() {
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-20 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <SiteBreadcrumbs className="mb-6" currentName="Η πλατφόρμα Banana" />
            <h1 className="font-display text-3xl leading-[1.1] md:text-5xl">
              Τέσσερις σουίτες προϊόντων, <em className="italic">ένα λειτουργικό σύστημα για το δίκτυό σας.</em>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">
              Η Banana βασίζεται σε τέσσερις πυλώνες: την ταυτότητά σας και τον τρόπο που τη μοιράζεστε, το AI Brain που θυμάται κάθε επαφή, ένα CRM που μετατρέπει τις συζητήσεις σε ευκαιρίες πώλησης και τις εφαρμογές με τις οποίες διαχειρίζεστε την επιχείρησή σας.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
                Ξεκινήστε — δωρεάν <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/el/pricing" className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70">
                Δείτε τις τιμές
              </Link>
            </div>
          </div>
          <div className="relative order-1 md:order-2">
            <div className="absolute -inset-6 rounded-[2rem] bg-brand-lime/10 blur-3xl" aria-hidden />
            <img src={heroImg} alt="Η πλατφόρμα Banana: επαγγελματικές κάρτες NFC, CRM, κρατήσεις, συμβάσεις και εργαλεία AI που λειτουργούν μαζί για ιδρυτές επιχειρήσεων και ομάδες πωλήσεων" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>

      {/* PILLARS GRID */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Τι περιλαμβάνει</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Όλα όσα χρειάζεστε, <em className="italic">σε ένα μέρος.</em>
            </h2>
            <p className="mt-4 text-muted-foreground">
              Επιλέξτε τον τίτλο οποιουδήποτε πυλώνα για την πλήρη επισκόπηση — ή μεταβείτε απευθείας σε ένα συγκεκριμένο εργαλείο.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <article key={p.href} className="flex flex-col rounded-2xl bg-card p-7 md:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                      <Icon className="h-5 w-5" />
                    </span>
                    <Link
                      to={p.href}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1.5 text-xs font-bold text-foreground transition hover:bg-foreground hover:text-background"
                      aria-label={`Ανοίξτε την επισκόπηση: ${p.title}`}
                    >
                      Επισκόπηση <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>

                  <h3 className="mt-6 font-display text-2xl leading-tight md:text-3xl">
                    <Link to={p.href} className="underline-offset-4 hover:underline">
                      {p.title}
                    </Link>
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-base">{p.desc}</p>

                  <div className="mt-6 border-t border-muted-foreground/10 pt-5">
                    <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                      Μεταβείτε απευθείας
                    </p>
                    <ul className="grid gap-2">
                      {p.links.map((l) => (
                        <li key={l.href}>
                          <Link
                            to={l.href}
                            className="group flex items-center justify-between gap-3 rounded-xl bg-muted px-4 py-3 text-sm font-bold transition hover:bg-foreground hover:text-background"
                          >
                            <span>{l.label}</span>
                            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUTRO */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Spark className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Μία πλατφόρμα. <em className="italic text-brand-lime">Κάθε στάδιο της σχέσης.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Από τη στιγμή που αγγίζετε για να μοιραστείτε την κάρτα σας μέχρι την ημέρα που κλείνετε τη συμφωνία — η Banana διατηρεί όλο το ιστορικό.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                Ξεκινήστε — δωρεάν
              </CtaLink>
              <Link to="/el/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Επικοινωνήστε με τις πωλήσεις
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
